import type { ObjectData, SceneStore } from '@os/doc';
import type { Point } from '@os/engine';
import type { CommentAnchor, CommentThread } from '../../lib/comment-types';

/** The anchor of a new pin: on the object under the click (offset from its corner), else on the point; on the active page. */
export function anchorAt(store: SceneStore, at: Point, objectId: string | null): CommentAnchor {
  const pageId = store.page;
  const o = objectId ? store.getObject(objectId) : undefined;
  if (!o || !objectId) return { x: at.x, y: at.y, pageId };
  return { objectId, dx: at.x - o.x, dy: at.y - o.y, x: at.x, y: at.y, pageId };
}

/**
 * The page a pin shows on (P14 Ruling 11): its object's page while the object exists, else the page it was put on,
 * else (a thread from before pages, or a deleted page) the first page.
 */
export function anchorPage(anchor: CommentAnchor, store: SceneStore): string {
  const live = 'objectId' in anchor ? store.pageOf(anchor.objectId) : undefined;
  if (live) return live;
  const pages = store.pages();
  return anchor.pageId !== undefined && pages.some((p) => p.id === anchor.pageId) ? anchor.pageId : pages[0]!.id;
}

export const threadsOnPage = (threads: readonly CommentThread[], store: SceneStore, page: string): CommentThread[] =>
  threads.filter((th) => anchorPage(th.anchor, store) === page);

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * Where a pin is drawn (world): on its object as it is now, kept inside the object's box; at the creation point
 * once the object is gone (`orphan`), so a deleted object never loses its threads (P7 Ruling 4).
 */
export function pinPosition(anchor: CommentAnchor, object: Readonly<ObjectData> | undefined): { x: number; y: number; orphan: boolean } {
  if (!('objectId' in anchor)) return { x: anchor.x, y: anchor.y, orphan: false };
  if (!object) return { x: anchor.x, y: anchor.y, orphan: true };
  return { x: object.x + clamp(anchor.dx, 0, object.w), y: object.y + clamp(anchor.dy, 0, object.h), orphan: false };
}
